import { observer } from 'mobx-react'
import React, { useEffect, useState } from 'react'

import UserStore from '../../stores/UserStore'

import LoadingIndicator from '../../components/atoms/loadingIndicator'
import BottomBarComponent from './BottomBarComponent'
import backendApis from '../../utils/backendApis'
import BannerSwiperComponent from './bannerSwiperComponent'
import ComponentTitle from './ComponentTitle'
import MainToonComponent from './mainToonComponent'
import QuicklinkComponent from './QuicklinkComponent'
import { useNavigate } from 'react-router-dom'

const MainComponent = observer(() => {
  const navigate = useNavigate()

  const [isLoaded, setIsLoaded] = useState(false)
  const [bannerData, setBannerData] = useState([])
  const [shortBannerData, setShortBannerData] = useState([])
  const [trendingToons, setTrendingToons] = useState([])
  const [newMainToons, setNewMainToons] = useState([])
  const [commentRecommendToons, setCommentRecommendToons] = useState([])
  const [previewToons, setPreviewToons] = useState([])

  const loadScreen = async () => {
    setIsLoaded(true)
    UserStore.setIsLoaded(true)
  }

  // 유틸리티 함수들
  const createToonMap = (toonResult) => {
    return new Map(toonResult.data.map((toon) => [toon._id, toon]))
  }

  const mapToonDetailsToList = (toonList, toonMap) => {
    return toonList.map((toon) => ({
      ...toon,
      toonDetails: toonMap.get(toon.toonId),
    }))
  }

  // 개별 데이터 fetch 함수들
  const fetchBannerData = async () => {
    const response = await backendApis.loadBannerData()
    if (response?.status === 200) {
      const filteredBannerData = response.data?.filter(
        (banner) =>
          !banner?.bannerType &&
          banner?.itemId?.toString() !== '65e0160e3401440ed9cecde3' &&
          !banner?.isMainEvent &&
          banner?.eventId?.toString() !== '6788cd5aba81c209cb3cc85d' &&
          banner?.eventId?.toString() !== '67a1b15290807ad55f000b7e'
      )
      const filteredShortBannerData = response.data?.filter(
        (banner) => banner?.bannerType === 'mainShortBanner'
      )
      return { filteredBannerData, filteredShortBannerData }
    }
    return { filteredBannerData: [], filteredShortBannerData: [] }
  }

  const fetchToonData = async () => {
    try {
      // 배너 데이터 로드
      const { filteredBannerData, filteredShortBannerData } = await fetchBannerData()
      setBannerData(filteredBannerData)
      setShortBannerData(filteredShortBannerData)

      // 모든 웹툰 로드
      const toonResult = await backendApis.loadAllToons()
      if (toonResult?.status !== 200) return

      // 기본 웹툰 설정
      const toonMap = createToonMap(toonResult)

      // 각 카테고리별 웹툰 데이터 로드
      const categories = [
        {
          api: backendApis.loadTrendingToons,
          setter: setTrendingToons,
          params: [],
        },
        {
          api: backendApis.loadRecommendingToons,
          setter: setNewMainToons,
          params: ['hot', 'mainNew'],
        },
        {
          api: backendApis.loadRecommendingToons,
          setter: setCommentRecommendToons,
          params: ['hot', 'commentRecommend'],
        },
        {
          api: backendApis.loadRecommendingToons,
          setter: setPreviewToons,
          params: ['hot', 'preview'],
        },
      ]

      await Promise.all(
        categories.map(async ({ api, setter, params }) => {
          const response = await api(...params)
          if (response?.status === 200) {
            const mappedData = mapToonDetailsToList(response.data, toonMap)
            setter(mappedData)
          }
        })
      )
    } catch (error) {
      console.error('웹툰 데이터 로드 중 오류 발생:', error)
    }
  }

  const navigateToPage = async () => {
    const toonIdFromParams = new URLSearchParams(window.location.search).get('toonId')
    const eventIdFromParams = new URLSearchParams(window.location.search).get('eventId')

    if (toonIdFromParams) {
      navigate(`/toon-detail/${toonIdFromParams}`)
    } else if (eventIdFromParams) {
      navigate(`/event/${eventIdFromParams}`)
    }
  }

  useEffect(() => {
    fetchToonData()
    navigateToPage()
    loadScreen()
  }, [])

  const HeaderComponent = () => {
    return (
      <section className="z-10 h-16 flex flex-row items-center justify-between overflow-hidden">
        <div className="w-16 p-2 whitespace-nowrap" />
        <div className="flex font-bold">올툰</div>
        <div className="w-16 p-2 whitespace-nowrap" />
      </section>
    )
  }

  /**
   * 새로운 작품 등장!
   */
  const NewToonList = () => {
    return (
      <div className="py-[4vw] mx-[4vw]">
        <ComponentTitle text="새로운 작품 등장!" />
        {newMainToons?.length > 0 && (
          <div className="grid grid-cols-3 gap-[2vw]">
            {newMainToons?.slice(0, 6)?.map((toon, index) => (
              <MainToonComponent
                key={toon?._id}
                toon={toon}
                index={index}
                type="new"
                component="main_new"
              />
            ))}
          </div>
        )}
        {/* <div>나에게 맞는 신작 더보기</div> */}
      </div>
    )
  }

  /**
   * 오늘 인기 TOP 10
   */
  const TrendingToonList = () => {
    if (trendingToons?.length > 0) {
      return (
        <div className="pl-[4vw] py-[4vw]">
          <ComponentTitle text="오늘 인기 TOP 10" />

          <div className="flex items-center overflow-x-scroll whitespace-nowrap hide-scrollbar">
            {trendingToons?.slice(0, 10).map((toon, index) => (
              <MainToonComponent
                key={toon?.toonId}
                toon={toon?.toonDetails}
                index={index}
                type="trending"
                component="main_trending"
              />
            ))}
          </div>
        </div>
      )
    }
  }

  /**
   * 독자 픽! TOP 3
   */
  const CommentRecommendComponent = () => {
    return (
      <div className="pl-[4vw] py-[4vw]">
        <ComponentTitle text="독자 픽! TOP 3" />

        <div className="flex items-center overflow-x-scroll justify-start hide-scrollbar">
          {commentRecommendToons?.slice(0, 3).map((toon, index) => (
            <MainToonComponent
              key={toon?._id}
              toon={toon}
              index={index}
              type="commentRecommend"
              component="main_commentRecommend"
            />
          ))}
        </div>
      </div>
    )
  }

  /**
   * 미리보기 한 컷!
   */
  const PreviewComponent = () => {
    return (
      <div className="pl-[4vw] py-[4vw]">
        <ComponentTitle text="미리보기 한 컷!" />

        <div className="flex items-center overflow-x-scroll justify-start hide-scrollbar">
          {previewToons?.slice(0, 3).map((toon, index) => (
            <MainToonComponent
              key={toon?._id}
              toon={toon}
              index={index}
              type="preview"
              component="main_preview"
            />
          ))}
        </div>
      </div>
    )
  }

  if (!isLoaded) {
    return <LoadingIndicator isLoading />
  }

  return (
    <div className="w-full overflow-y-scroll" style={{ height: '100vh' }}>
      <HeaderComponent />

      {bannerData?.length > 0 && (
        <BannerSwiperComponent bannerData={bannerData} bannerType="main" />
      )}

      <QuicklinkComponent />
      <NewToonList />

      {shortBannerData?.length > 0 && (
        <BannerSwiperComponent bannerData={shortBannerData} bannerType="short" />
      )}

      <TrendingToonList />

      <CommentRecommendComponent />
      <PreviewComponent />

      <div className="h-[20vw]" />

      <BottomBarComponent />
    </div>
  )
})

export default MainComponent
